import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  MenuItem,
  InputLabel,
  Select,
  Alert,
  Stack,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, CameraAlt as CameraAltIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import PatientService from '../../services/PatientService.js';
import EnumService from '../../services/EnumService';
import LocationService from '../../services/LocationService';
import Tesseract from 'tesseract.js';

const normalizeListResponse = (response) => {
  const data = response?.data ?? response;
  if (Array.isArray(data)) {
    const nested = data[0]?.data;
    if (Array.isArray(nested)) return nested;
    return data;
  }
  if (data && typeof data === 'object') return [data];
  return [];
};

const dedupeByKey = (items, getKey) => {
  const map = new Map();
  for (const item of Array.isArray(items) ? items : []) {
    const key = getKey(item);
    if (key === null || key === undefined || key === '') continue;
    if (!map.has(key)) map.set(key, item);
  }
  return Array.from(map.values());
};

const normalizeStates = (response) => {
  const items = normalizeListResponse(response);
  return dedupeByKey(items, (state) => state?.stateId ?? state?.id ?? state?.Id ?? (state?.name ? `name:${String(state.name).trim().toLowerCase()}` : null));
};

const normalizeCities = (response) => {
  const items = normalizeListResponse(response);
  return dedupeByKey(items, (city) => city?.id ?? city?.cityId ?? city?.Id ?? (city?.name ? `name:${String(city.name).trim().toLowerCase()}` : null));
};

const PatientForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const locationService = useMemo(() => LocationService, []);
  
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  
  // Location data states
  const [masterCountryList, setMasterCountryList] = useState([]); // Master list of all countries
  const [countries, setCountries] = useState([]); // List for Autocomplete (can be filtered/searched)
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [scanActive, setScanActive] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanError, setScanError] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const scanWorkerRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const scanBusyRef = useRef(false);
  const scanActiveRef = useRef(false);
  const scanStabilityRef = useRef({ nric: null, hits: 0 });
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    dateOfBirth: null,
    gender: 'Male',
    phoneNumber: '',
    email: '',
    address: '',
    country: '',
    countryId: 0,
    state: '',
    stateId: 0,
    city: '',
    cityId: 0,
    postalCode: '',
    isActive: true,
    emergencyContactName: '',
    emergencyContactPhone: '',
    insuranceProvider: '',
    insurancePolicyNumber: '',
    officialDocumentType: '', // Use empty string as default until document types are loaded
    officialDocumentNumber: '',
    bloodType: '',
    allergies: '',
    medicalConditions: '',
    medicalRecordNumber: '' // Auto-generated by backend, displayed for reference
  });
  
  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    email: '',
    address: '',
    country: '',
    state: '',
    city: '',
    postalCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    officialDocumentNumber: '',
    bloodType: '',
    allergies: '',
    medicalConditions: '',
    insuranceProvider: '',
    insurancePolicyNumber: ''
  });
  
  // Selected location objects for Autocomplete
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const isMyKadSelected = useMemo(() => {
    const selected = documentTypes.find(dt => (dt.id ?? dt.Id) === formData.officialDocumentType);
    const name = selected ? (selected.name ?? selected.Name) : '';
    const idVal = String(formData.officialDocumentType);
    return (name && name.toLowerCase() === 'mykad') || idVal === '1';
  }, [documentTypes, formData.officialDocumentType]);
  
  // Effect 1: Fetch initial non-dependent data (document types and countries)
  useEffect(() => {
    const fetchInitialLookups = async () => {
      // Fetch document types
      try {
        const docTypesResponse = await EnumService.getOfficialDocumentTypes();
        // Ensure docTypesResponse and docTypesResponse.data are valid before accessing
        if (docTypesResponse && docTypesResponse.data) {
          setDocumentTypes(docTypesResponse.data);
          setFormData(prevData => {
            // Check if response.data is an array and has length
            if (Array.isArray(docTypesResponse.data) && docTypesResponse.data.length > 0 && prevData.officialDocumentType === '') {
              return { ...prevData, officialDocumentType: docTypesResponse.data[0].id };
            }
            return prevData;
          });
        } else {
          console.error('Document types response is invalid:', docTypesResponse);
          setDocumentTypes([]); // Set to empty array if response is invalid
        }
      } catch (error) {
        console.error('Error fetching document types:', error);
        setDocumentTypes([]); // Set to empty array on error
      }

      // Fetch countries
      try {
        setLoadingCountries(true);
        const countriesResponse = await locationService.getAllCountries();
        const countriesData = Array.isArray(countriesResponse) 
                              ? countriesResponse 
                              : [];
        setMasterCountryList(countriesData); // Populate master list
        setCountries(countriesData);         // Populate display list for Autocomplete
      } catch (error) {
        console.error('Error fetching countries:', error);
        setMasterCountryList([]); // Ensure empty on error
        setCountries([]);         // Ensure empty on error
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchInitialLookups();
  }, [locationService]); // Runs once on mount, include locationService
  
  // Effect: Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (selectedCountry) {
        try {
          setLoadingStates(true);
          setCities([]);
          setSelectedCity(null);
          setSelectedState(null);
          
          // Update form data with country info - store the actual country name
          setFormData(prev => ({
            ...prev,
            country: selectedCountry.name, // Store the actual country name
            countryId: selectedCountry.id,
            state: '',
            stateId: 0,
            city: '',
            cityId: 0
          }));
          
          const statesResponse = await locationService.getStatesByCountry(selectedCountry.id); // Use instance
          
          const normalizedStates = normalizeStates(statesResponse);
          setStates(normalizedStates);
          setLoadingStates(false);
        } catch (error) {
          console.error('Error fetching states:', error);
          setLoadingStates(false);
        }
      } else {
        setStates([]);
        setCities([]);
      }
    };
    
    fetchStates();
  }, [selectedCountry, locationService]); // Add locationService
  
  // Effect: Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (selectedState) {
        try {
          setLoadingCities(true);
          setSelectedCity(null);
          const stateId =
            selectedState.stateId ??
            selectedState.id ??
            selectedState.Id;
          
          // Update form data with state info - store the actual state name
          setFormData(prev => ({
            ...prev,
            state: selectedState.name, // Store the actual state name
            stateId: stateId || 0,
            city: '',
            cityId: 0
          }));
          
          if (!stateId) {
            setCities([]);
            setLoadingCities(false);
            return;
          }

          const citiesResponse = await locationService.getCitiesByState(stateId); // Use instance
          setCities(normalizeCities(citiesResponse));
          setLoadingCities(false);
        } catch (error) {
          console.error('Error fetching cities:', error);
          setLoadingCities(false);
        }
      } else {
        setCities([]);
      }
    };
    
    fetchCities();
  }, [selectedState, locationService]); // Add locationService
  
  // Effect: Update form data when city is selected
  useEffect(() => {
    if (selectedCity) {
      setFormData(prev => {
        const updatedFormData = {
          ...prev,
          city: selectedCity.name, // Store the actual city name
          cityId: selectedCity.id || selectedCity.cityId // Try both property names
        };
        return updatedFormData;
      });
    }
  }, [selectedCity]);

  // Effect 2: Load patient data when in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchPatientData = async () => {
        setLoading(true); // For patient data loading
        setError(null);
        try {
          const response = await PatientService.getPatientById(parseInt(id));
          const patient = response.data;

          // Set form data from patient, preserving already set fields like officialDocumentType
          setFormData(prevFormData => ({
            ...prevFormData,
            ...patient,
            dateOfBirth: patient.dateOfBirth ? new Date(patient.dateOfBirth) : null,
          }));
          
          // Set location data for Autocomplete components
          if (patient.countryId && patient.country) {
            const countryObj = { id: patient.countryId, name: patient.country };
            setSelectedCountry(countryObj);
            
            // Fetch states for this country
            try {
              setLoadingStates(true);
              const statesResponse = await locationService.getStatesByCountry(patient.countryId); // Use instance
              setStates(normalizeStates(statesResponse));
              setLoadingStates(false);
              
              // Set state if available
              if (patient.stateId && patient.state) {
                const stateObj = { stateId: patient.stateId, name: patient.state };
                setSelectedState(stateObj);
                
                // Fetch cities for this state
                try {
                  setLoadingCities(true);
                  const citiesResponse = await locationService.getCitiesByState(patient.stateId); // Use instance
                  
                  const normalizedCities = normalizeCities(citiesResponse);
                  setCities(normalizedCities);
                  
                  setLoadingCities(false);
                  
                  // Set city if available
                  if (patient.cityId && patient.city) {
                    const cityObj = { id: patient.cityId, name: patient.city };
                    setSelectedCity(cityObj);
                  } else if (patient.city && (!patient.cityId || patient.cityId === 0)) {
                    // Special case: We have a city name but no valid cityId
                    // Try to find the city by name in the cities array
                    const foundCity = normalizedCities.find(city => city.name === patient.city);
                    if (foundCity) {
                      setSelectedCity(foundCity);
                    } else {
                      // If city not found in the array, create a temporary object with the name
                      const tempCityObj = { name: patient.city };
                      setSelectedCity(tempCityObj);
                    }
                  }
                } catch (error) {
                  console.error('Error fetching cities for patient state:', error);
                  setLoadingCities(false);
                }
              }
            } catch (error) {
              console.error('Error fetching states for patient country:', error);
              setLoadingStates(false);
            }
          }

        } catch (error) {
          console.error('Error fetching patient data for edit:', error);
          setError('Failed to load patient data. Please try again later.');
        } finally {
          setLoading(false);
        }
      };
      
      // Only fetch if patient data (identified by formData.id) hasn't been loaded for this `id` yet.
      if (formData.id !== parseInt(id)) {
          fetchPatientData();
      } else {
        // If formData.id matches, patient data is already loaded. Ensure loading flag is false.
        setLoading(false);
      }

    } else if (!isEditMode) {
      setLoading(false); // For new form, ensure loading is false.
    }
  }, [id, isEditMode, locationService, formData.id]); // Dependencies, added locationService and formData.id for stability

  // Handle input changes for form fields
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      dateOfBirth: date
    });
  };
  const openScanDialog = async () => {
    setScanError('');
    setScanResult(null);
    setScanProgress(0);
    scanStabilityRef.current = { nric: null, hits: 0 };
    setScanOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setMediaStream(stream);
        scanBusyRef.current = false;
        scanActiveRef.current = true;
        setScanActive(true);
        if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = setInterval(() => {
          if (!scanActiveRef.current) return;
          if (scanBusyRef.current) return;
          void processScanFrame();
        }, 1600);
      }
    } catch (e) {
      console.error('Camera access error:', e);
      // Check if the error is likely due to non-secure context
      if (window.location.protocol !== 'https:' && 
          window.location.hostname !== 'localhost' && 
          window.location.hostname !== '127.0.0.1') {
        setScanError('Camera access requires HTTPS or localhost. For testing on network, configure your browser to treat this origin as secure.');
      } else {
        setScanError(`Unable to access camera: ${e.message || 'Permission denied'}`);
      }
    }
  };
  const closeScanDialog = () => {
    scanActiveRef.current = false;
    setScanActive(false);
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    const worker = scanWorkerRef.current;
    scanWorkerRef.current = null;
    if (worker) worker.terminate();
    scanBusyRef.current = false;
    if (mediaStream) {
      mediaStream.getTracks().forEach(t => t.stop());
      setMediaStream(null);
    }
    setScanOpen(false);
    setOcrLoading(false);
  };
  const parseMyKadOcr = (t) => {
    const rawText = (t || '').replace(/\r/g, '\n');
    const lines = rawText.split(/\n+/).map(l => l.trim()).filter(Boolean);

    const digitFixMap = {
      O: '0',
      Q: '0',
      D: '0',
      I: '1',
      L: '1',
      '|': '1',
      S: '5',
      B: '8',
      Z: '2',
      G: '6'
    };

    const normalizeToDigits = (s) => {
      return (s || '')
        .toUpperCase()
        .split('')
        .map(ch => (digitFixMap[ch] ? digitFixMap[ch] : ch))
        .join('')
        .replace(/[^0-9]/g, '');
    };

    const formatNric = (digits12) => {
      return digits12.slice(0, 6) + '-' + digits12.slice(6, 8) + '-' + digits12.slice(8);
    };

    const tryParseDobFromDigits = (digits12) => {
      const yy = parseInt(digits12.slice(0, 2), 10);
      const mm = parseInt(digits12.slice(2, 4), 10);
      const dd = parseInt(digits12.slice(4, 6), 10);
      if (!Number.isFinite(yy) || !Number.isFinite(mm) || !Number.isFinite(dd)) return null;
      if (mm < 1 || mm > 12) return null;
      if (dd < 1 || dd > 31) return null;
      const nowYY = new Date().getFullYear() % 100;
      const century = yy > nowYY ? 1900 : 2000;
      const fullYear = century + yy;
      const candidate = new Date(fullYear, mm - 1, dd);
      if (Number.isNaN(candidate.getTime())) return null;
      if (candidate.getFullYear() !== fullYear || candidate.getMonth() !== mm - 1 || candidate.getDate() !== dd) return null;
      const today = new Date();
      const oldest = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
      if (candidate > today) return null;
      if (candidate < oldest) return null;
      return candidate;
    };

    const possibleNricMatches = [
      ...(rawText.match(/[0-9OILSZBG|]{6}[-\s]*[0-9OILSZBG|]{2}[-\s]*[0-9OILSZBG|]{4}/gi) || []),
      ...(rawText.match(/[0-9OILSZBG|]{12}/gi) || [])
    ];

    let nric = null;
    let dob = null;
    let gender = null;
    for (const match of possibleNricMatches) {
      const digits = normalizeToDigits(match);
      if (digits.length !== 12) continue;
      const candidateDob = tryParseDobFromDigits(digits);
      if (!candidateDob) continue;
      nric = formatNric(digits);
      dob = candidateDob;
      const genderDigit = parseInt(digits.slice(-1), 10);
      if (Number.isFinite(genderDigit)) gender = genderDigit % 2 === 1 ? 'Male' : 'Female';
      break;
    }

    let name = null;
    let firstName = null;
    let lastName = null;
    const isNoiseLine = (l) => {
      const u = (l || '').toUpperCase();
      if (u.includes('MALAYSIA')) return true;
      if (u.includes('KAD') || u.includes('PENGENALAN') || u.includes('IDENTITY')) return true;
      if (u.includes('WARGANEGARA') || u.includes('CITIZEN')) return true;
      if (u.includes('LELAKI') || u.includes('PEREMPUAN')) return true;
      return false;
    };

    const labelIdx = lines.findIndex(l => /\b(NAMA|NAME)\b/i.test(l));
    if (labelIdx >= 0) {
      const sameLine = lines[labelIdx].replace(/.*\b(?:NAMA|NAME)\b[:\s-]*/i, '').trim();
      if (sameLine && !/\d/.test(sameLine) && !isNoiseLine(sameLine)) name = sameLine;
      if (!name) {
        const next = lines[labelIdx + 1];
        if (next && !/\d/.test(next) && !isNoiseLine(next)) name = next;
      }
    }

    if (!name) {
      const candidates = lines
        .map(l => l.replace(/[^A-Za-z' -]/g, ' ').replace(/\s{2,}/g, ' ').trim())
        .filter(l => l.length >= 8)
        .filter(l => l.split(/\s+/).filter(Boolean).length >= 2)
        .filter(l => !isNoiseLine(l));

      name = candidates.sort((a, b) => b.length - a.length)[0] || null;
    }

    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length > 1) {
        lastName = parts[parts.length - 1];
        firstName = parts.slice(0, -1).join(' ');
      } else {
        firstName = name;
        lastName = '';
      }
    }
    return { nric, dob, gender, firstName, lastName };
  };
  const ensureScanWorker = async () => {
    if (scanWorkerRef.current) return scanWorkerRef.current;
    const worker = await Tesseract.createWorker('eng', Tesseract.OEM.DEFAULT, {
      logger: (m) => {
        if (m && typeof m.progress === 'number') setScanProgress(m.progress);
      }
    });
    await worker.setParameters({
      tessedit_pageseg_mode: Tesseract.PSM.SPARSE_TEXT,
      tessedit_char_whitelist: "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789- '",
      preserve_interword_spaces: '0',
      user_defined_dpi: '300'
    });
    scanWorkerRef.current = worker;
    return worker;
  };

  const drawAndPreprocessFrame = (mode = 'grayscale') => {
    if (!videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    const roiW = Math.round(vw * 0.92);
    const roiH = Math.round(vh * 0.56);
    const roiX = Math.round((vw - roiW) / 2);
    const roiY = Math.round((vh - roiH) / 2);

    const targetW = 960;
    const targetH = Math.max(540, Math.round(targetW * (roiH / roiW)));
    canvas.width = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, roiX, roiY, roiW, roiH, 0, 0, targetW, targetH);

    const imageData = ctx.getImageData(0, 0, targetW, targetH);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
      const r = d[i];
      const g = d[i + 1];
      const b = d[i + 2];
      let v = 0.299 * r + 0.587 * g + 0.114 * b;
      v = (v - 128) * 1.35 + 128;
      if (mode === 'binary') v = v > 145 ? 255 : 0;
      const vv = v < 0 ? 0 : v > 255 ? 255 : v;
      d[i] = vv;
      d[i + 1] = vv;
      d[i + 2] = vv;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const processScanFrame = async () => {
    scanBusyRef.current = true;
    setOcrLoading(true);
    setScanError('');
    try {
      const worker = await ensureScanWorker();
      const frame = drawAndPreprocessFrame('grayscale');
      if (!frame) return;

      const { data: data1 } = await worker.recognize(frame, { rotateAuto: true });
      const text1 = (data1 && data1.text) ? data1.text : '';
      let parsed = parseMyKadOcr(text1);
      let data = data1;
      let text = text1;

      if (!(parsed && parsed.nric)) {
        const binaryFrame = drawAndPreprocessFrame('binary');
        if (binaryFrame) {
          const { data: data2 } = await worker.recognize(binaryFrame, { rotateAuto: true });
          const text2 = (data2 && data2.text) ? data2.text : '';
          const parsed2 = parseMyKadOcr(text2);
          if (parsed2 && parsed2.nric) {
            parsed = parsed2;
            data = data2;
            text = text2;
          }
        }
      }

      if (parsed && parsed.nric) {
        const prev = scanStabilityRef.current;
        if (prev.nric === parsed.nric) {
          prev.hits += 1;
        } else {
          scanStabilityRef.current = { nric: parsed.nric, hits: 1 };
        }
        const stableHits = (scanStabilityRef.current.nric === parsed.nric) ? scanStabilityRef.current.hits : 1;
        const stable = stableHits >= 2;
        setScanResult({ text, parsed, confidence: data ? data.confidence : null, stable });
      }
    } catch (e) {
      setScanError('Unable to read MyKad. Please improve lighting and hold steady.');
    } finally {
      scanBusyRef.current = false;
      setOcrLoading(false);
    }
  };

  const toggleScanActive = () => {
    const next = !scanActiveRef.current;
    scanActiveRef.current = next;
    setScanActive(next);
  };

  const applyScanResult = () => {
    if (!scanResult || !scanResult.parsed || !scanResult.stable) return;
    const p = scanResult.parsed;
    setFormData(prev => ({
      ...prev,
      officialDocumentNumber: p.nric || prev.officialDocumentNumber,
      firstName: p.firstName || prev.firstName,
      lastName: p.lastName || prev.lastName,
      dateOfBirth: p.dob || prev.dateOfBirth,
      gender: p.gender || prev.gender
    }));
    closeScanDialog();
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setError(null);
    setSuccess(false);
    setFormErrors({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      phoneNumber: '',
      email: '',
      address: '',
      country: '',
      state: '',
      city: '',
      postalCode: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      officialDocumentNumber: '',
      bloodType: '',
      allergies: '',
      medicalConditions: '',
      insuranceProvider: '',
      insurancePolicyNumber: ''
    });
    
    // Validate all fields
    let hasErrors = false;
    const newFormErrors = {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      phoneNumber: '',
      email: '',
      address: '',
      country: '',
      state: '',
      city: '',
      postalCode: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      officialDocumentNumber: '',
      bloodType: '',
      allergies: '',
      medicalConditions: '',
      insuranceProvider: '',
      insurancePolicyNumber: ''
    };
    
    // Personal information validation
    if (!formData.firstName.trim()) {
      newFormErrors.firstName = 'First name is required';
      hasErrors = true;
    }
    
    if (!formData.lastName.trim()) {
      newFormErrors.lastName = 'Last name is required';
      hasErrors = true;
    }
    
    if (!formData.dateOfBirth) {
      newFormErrors.dateOfBirth = 'Date of birth is required';
      hasErrors = true;
    } else {
      // Check if date is valid and not in the future
      const today = new Date();
      if (formData.dateOfBirth > today) {
        newFormErrors.dateOfBirth = 'Date of birth cannot be in the future';
        hasErrors = true;
      }
    }
    
    // Contact information validation
    if (!formData.phoneNumber.trim()) {
      newFormErrors.phoneNumber = 'Phone number is required';
      hasErrors = true;
    } else if (!/^\+?[0-9\s-()]{8,20}$/.test(formData.phoneNumber.trim())) {
      newFormErrors.phoneNumber = 'Please enter a valid phone number';
      hasErrors = true;
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newFormErrors.email = 'Please enter a valid email address';
      hasErrors = true;
    }
    
    // Address validation
    if (!formData.address.trim()) {
      newFormErrors.address = 'Address is required';
      hasErrors = true;
    }
    
    // Country validation
    if (!selectedCountry && !formData.countryId) {
      newFormErrors.country = 'Please select a country';
      hasErrors = true;
    }
    
    // State validation - only required if the selected country has states
    if ((selectedCountry || formData.countryId) && states.length > 0 && !selectedState && !formData.stateId) {
      newFormErrors.state = 'Please select a state/province';
      hasErrors = true;
    }
    
    // City validation - only required if the selected state has cities
    if ((selectedState || formData.stateId) && cities.length > 0 && !selectedCity && !formData.cityId) {
      newFormErrors.city = 'Please select a city';
      hasErrors = true;
    }
    
    // Postal code validation
    if (!formData.postalCode.trim()) {
      newFormErrors.postalCode = 'Postal code is required';
      hasErrors = true;
    }
    
    // Emergency contact validation
    if (!formData.emergencyContactName.trim()) {
      newFormErrors.emergencyContactName = 'Emergency contact name is required';
      hasErrors = true;
    }
    
    if (!formData.emergencyContactPhone.trim()) {
      newFormErrors.emergencyContactPhone = 'Emergency contact phone is required';
      hasErrors = true;
    } else if (!/^\+?[0-9\s-()]{8,20}$/.test(formData.emergencyContactPhone.trim())) {
      newFormErrors.emergencyContactPhone = 'Please enter a valid phone number';
      hasErrors = true;
    }
    
    // Official document validation
    if (formData.officialDocumentType && !formData.officialDocumentNumber.trim()) {
      newFormErrors.officialDocumentNumber = 'Document number is required when document type is selected';
      hasErrors = true;
    }
    
    // Blood type validation - not required but validate format if provided
    if (formData.bloodType && !['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', ''].includes(formData.bloodType)) {
      newFormErrors.bloodType = 'Please select a valid blood type';
      hasErrors = true;
    }
    
    // Allergies validation - validate length if provided
    if (formData.allergies && formData.allergies.length > 500) {
      newFormErrors.allergies = 'Allergies information is too long (maximum 500 characters)';
      hasErrors = true;
    }
    
    // Medical conditions validation - validate length if provided
    if (formData.medicalConditions && formData.medicalConditions.length > 500) {
      newFormErrors.medicalConditions = 'Medical conditions information is too long (maximum 500 characters)';
      hasErrors = true;
    }
    
    // Insurance validation - if policy number is provided, provider must also be provided
    if (formData.insurancePolicyNumber && !formData.insuranceProvider) {
      newFormErrors.insuranceProvider = 'Insurance provider is required when policy number is provided';
      hasErrors = true;
    }
    
    // Insurance policy number validation - if provider is provided, policy number should also be provided
    if (formData.insuranceProvider && !formData.insurancePolicyNumber) {
      newFormErrors.insurancePolicyNumber = 'Policy number is required when insurance provider is provided';
      hasErrors = true;
    }
    
    // Update form errors
    setFormErrors(newFormErrors);
    
    // If there are validation errors, don't proceed with submission
    if (hasErrors) {
      return;
    }
    
    setSaving(true);
    
    try {
      // Format date for API
      const formattedData = {
        ...formData,
        dateOfBirth: formData.dateOfBirth ? formData.dateOfBirth.toISOString().split('T')[0] : null, // YYYY-MM-DD
      };
      
      if (isEditMode) {
        // Update existing patient
        await PatientService.updatePatient(parseInt(id), formattedData);
      } else {
        // Create new patient
        await PatientService.createPatient(formattedData);
      }
      
      setSuccess(true);
      
      // Redirect after a short delay
      setTimeout(() => {
        if (isEditMode) {
          navigate(`/patients/${id}`);
        } else {
          navigate('/patients');
        }
      }, 1500);
    } catch (error) {
      console.error('Error saving patient:', error);
      setError('Failed to save patient. Please try again.');
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          sx={{ mr: 1 }}
          component={RouterLink}
          to={isEditMode ? `/patients/${id}` : '/patients'}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Patient' : 'Add New Patient'}
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Patient {isEditMode ? 'updated' : 'created'} successfully!
        </Alert>
      )}
      
      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Medical Record Number"
                  name="medicalRecordNumber"
                  value={formData.medicalRecordNumber || (isEditMode ? '' : 'Will be auto-generated')}
                  disabled={true} // Always disabled as it's auto-generated
                  helperText={isEditMode ? "This field is auto-generated by the system." : "A unique medical record number will be automatically generated when you save this patient."}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nickname"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleInputChange}
                  disabled={saving}
                  helperText="Optional preferred name"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Date of Birth"
                    value={formData.dateOfBirth}
                    onChange={handleDateChange}
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        disabled: saving,
                        error: !!formErrors.dateOfBirth,
                        helperText: formErrors.dateOfBirth
                      }
                    }}
                    disabled={saving}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel id="gender-label">Gender</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="gender-label"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    <FormControlLabel value="Male" control={<Radio />} label="Male" disabled={saving} />
                    <FormControlLabel value="Female" control={<Radio />} label="Female" disabled={saving} />
                    <FormControlLabel value="Other" control={<Radio />} label="Other" disabled={saving} />
                  </RadioGroup>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.phoneNumber}
                  helperText={formErrors.phoneNumber || "Format: +1 (555) 123-4567"}
                />
              </Grid>
              

              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="official-document-type-label">Official Document Type</InputLabel>
                  <Select
                    labelId="official-document-type-label"
                    name="officialDocumentType"
                    value={formData.officialDocumentType}
                    onChange={handleInputChange}
                    label="Official Document Type"
                    disabled={saving}
                    required
                  >
                    {documentTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Official Document Number"
                  name="officialDocumentNumber"
                  required
                  value={formData.officialDocumentNumber}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.officialDocumentNumber}
                  helperText={formErrors.officialDocumentNumber || "ID number from the selected document type"}
                  InputProps={{
                    endAdornment: isMyKadSelected ? (
                      <Tooltip title="Scan MyKad">
                        <span>
                          <IconButton onClick={openScanDialog} disabled={saving} size="small">
                            <CameraAltIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Address"
                  name="address"
                  multiline
                  rows={2}
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.address}
                  helperText={formErrors.address}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  id="country-select"
                  options={Array.isArray(countries) ? countries : []}
                  loading={loadingCountries}
                  getOptionLabel={(option) => option && option.name ? option.name : ''}
                  value={selectedCountry}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  onChange={(event, newValue) => {
                    setSelectedCountry(newValue);
                    // Clear country error when a selection is made
                    if (newValue && formErrors.country) {
                      setFormErrors(prev => ({
                        ...prev,
                        country: ''
                      }));
                    }
                  }}
                  onInputChange={async (event, newInputValue, reason) => {
                    if (reason === 'clear') {
                      setCountries(masterCountryList); // Revert to the full list when cleared
                      setLoadingCountries(false);
                      return;
                    }
                    if (reason === 'input') {
                      if (newInputValue) {
                        setLoadingCountries(true);
                        try {
                          const response = await locationService.searchCountries(newInputValue);
                          const searchedCountries = response?.data && Array.isArray(response.data) ? response.data : [];
                          // If search returns results, use them. If search is empty, revert to master list.
                          setCountries(searchedCountries.length > 0 ? searchedCountries : masterCountryList);
                        } catch (error) {
                          console.error('Error searching countries:', error);
                          setCountries(masterCountryList); // Fallback to master list on error
                        } finally {
                          setLoadingCountries(false);
                        }
                      } else {
                        // Input is empty (e.g., user deleted all text but didn't click "clear")
                        setCountries(masterCountryList); // Revert to master list
                        setLoadingCountries(false);
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      error={!!formErrors.country}
                      helperText={formErrors.country}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingCountries ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  id="state-select"
                  options={Array.isArray(states) ? states : []}
                  loading={loadingStates}
                  getOptionLabel={(option) => option && option.name ? option.name : ''}
                  value={selectedState}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.stateId === value.stateId;
                  }}
                  onChange={(event, newValue) => {
                    setSelectedState(newValue);
                    // Clear state error when a selection is made
                    if (newValue && formErrors.state) {
                      setFormErrors(prev => ({
                        ...prev,
                        state: ''
                      }));
                    }
                  }}
                  onInputChange={async (event, newInputValue) => {
                    if (selectedCountry && newInputValue) {
                      setLoadingStates(true);
                      try {
                        const response = await locationService.searchStates(selectedCountry.id, newInputValue);
                        setStates(normalizeStates(response));
                      } catch (error) {
                        console.error('Error searching states:', error);
                        setStates([]);
                      } finally {
                        setLoadingStates(false);
                      }
                    } else if (selectedCountry) {
                      // If input is cleared but country is selected, load all states for that country
                      setLoadingStates(true);
                      try {
                        const response = await locationService.getStatesByCountry(selectedCountry.id);
                        setStates(normalizeStates(response));
                      } catch (error) {
                        console.error('Error loading states:', error);
                        setStates([]);
                      } finally {
                        setLoadingStates(false);
                      }
                    }
                  }}
                  noOptionsText="No states found for this country"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State/Province"
                      error={!!formErrors.state}
                      helperText={formErrors.state}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  disabled={saving || !selectedCountry}
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  id="city-select"
                  options={Array.isArray(cities) ? cities : []}
                  loading={loadingCities}
                  getOptionLabel={(option) => option && option.name ? option.name : ''}
                  value={selectedCity}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) {
                      return false;
                    }
                    // Check both id and cityId properties
                    const optionId = option.id || option.cityId;
                    const valueId = value.id || value.cityId;
                    const isEqual = optionId === valueId;
                    return isEqual;
                  }}
                  onChange={(event, newValue) => {
                    setSelectedCity(newValue);
                    // Clear city error when a selection is made
                    if (newValue && formErrors.city) {
                      setFormErrors(prev => ({
                        ...prev,
                        city: ''
                      }));
                    }
                  }}
                  onInputChange={async (event, newInputValue) => {
                    if (selectedState && newInputValue) {
                      setLoadingCities(true);
                      try {
                        const response = await locationService.searchCities(selectedState.stateId, newInputValue);
                        setCities(normalizeCities(response));
                      } catch (error) {
                        console.error('Error searching cities:', error);
                        setCities([]);
                      } finally {
                        setLoadingCities(false);
                      }
                    } else if (selectedState) {
                      // If input is cleared but state is selected, load all cities for that state
                      setLoadingCities(true);
                      try {
                        const response = await locationService.getCitiesByState(selectedState.stateId);
                        setCities(normalizeCities(response));
                      } catch (error) {
                        console.error('Error fetching cities for patient state:', error);
                      } finally {
                        setLoadingCities(false);
                      }
                    }
                  }}
                  noOptionsText="No cities found for this state"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      error={!!formErrors.city}
                      helperText={formErrors.city}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                  disabled={saving || !selectedState}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Postal Code"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.postalCode}
                  helperText={formErrors.postalCode}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Medical Information
                  </Typography>
                </Divider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Emergency Contact Name"
                  name="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.emergencyContactName}
                  helperText={formErrors.emergencyContactName}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Emergency Contact Phone"
                  name="emergencyContactPhone"
                  value={formData.emergencyContactPhone}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.emergencyContactPhone}
                  helperText={formErrors.emergencyContactPhone || "Format: +1 (555) 123-4567"}
                />
              </Grid>
              

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!formErrors.bloodType}>
                  <InputLabel id="blood-type-label">Blood Type</InputLabel>
                  <Select
                    labelId="blood-type-label"
                    name="bloodType"
                    value={formData.bloodType}
                    onChange={handleInputChange}
                    label="Blood Type"
                    disabled={saving}
                  >
                    <MenuItem value=""><em>Unknown</em></MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </Select>
                  {formErrors.bloodType && <FormHelperText>{formErrors.bloodType}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Allergies"
                  name="allergies"
                  multiline
                  rows={2}
                  value={formData.allergies}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.allergies}
                  helperText={formErrors.allergies || "List any allergies, or 'None' if none"}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Medical Conditions"
                  name="medicalConditions"
                  multiline
                  rows={2}
                  value={formData.medicalConditions}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.medicalConditions}
                  helperText={formErrors.medicalConditions || "List any chronic conditions, or 'None' if none"}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <FormLabel id="status-label">Status</FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="status-label"
                    name="isActive"
                    value={formData.isActive.toString()}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        isActive: e.target.value === 'true'
                      });
                    }}
                  >
                    <FormControlLabel value="true" control={<Radio />} label="Active" disabled={saving} />
                    <FormControlLabel value="false" control={<Radio />} label="Inactive" disabled={saving} />
                  </RadioGroup>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Insurance Information
                  </Typography>
                </Divider>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Insurance Provider"
                  name="insuranceProvider"
                  value={formData.insuranceProvider}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.insuranceProvider}
                  helperText={formErrors.insuranceProvider}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Insurance Policy Number"
                  name="insurancePolicyNumber"
                  value={formData.insurancePolicyNumber}
                  onChange={handleInputChange}
                  disabled={saving}
                  error={!!formErrors.insurancePolicyNumber}
                  helperText={formErrors.insurancePolicyNumber}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    component={RouterLink}
                    to={isEditMode ? `/patients/${id}` : '/patients'}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                  >
                    {saving ? <CircularProgress size={24} /> : (isEditMode ? 'Update Patient' : 'Create Patient')}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    <ScanDialog
      open={scanOpen}
      onClose={closeScanDialog}
      onToggleActive={toggleScanActive}
      onApply={applyScanResult}
      scanActive={scanActive}
      progress={scanProgress}
      videoRef={videoRef}
      canvasRef={canvasRef}
      loading={ocrLoading}
      error={scanError}
      result={scanResult}
    />
    </Box>
  );
};

const ScanDialog = ({ open, onClose, onToggleActive, onApply, scanActive, progress, videoRef, canvasRef, loading, error, result }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>Scan MyKad</DialogTitle>
    <DialogContent>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ position: 'relative' }}>
          <video ref={videoRef} style={{ width: '100%' }} playsInline muted />
          <Box
            sx={{
              position: 'absolute',
              left: '4%',
              top: '22%',
              width: '92%',
              height: '56%',
              border: '2px solid rgba(255,255,255,0.85)',
              borderRadius: 1,
              boxSizing: 'border-box'
            }}
          />
        </Box>
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </Box>
      {loading && !(result && result.parsed) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && typeof progress === 'number' && progress > 0 && progress < 1 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Scanning... {Math.round(progress * 100)}%
        </Typography>
      )}
      {!loading && scanActive && !(result && result.parsed && result.parsed.nric) && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Align the MyKad inside the box and hold steady.
        </Typography>
      )}
      {error && (
        <Alert severity="error">{error}</Alert>
      )}
      {result && result.parsed && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Recognized</Typography>
          {result.stable ? (
            <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
              Stable detection
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Hold still to confirm
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField label="NRIC" value={result.parsed.nric || ''} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Gender" value={result.parsed.gender || ''} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="First Name" value={result.parsed.firstName || ''} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Last Name" value={result.parsed.lastName || ''} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Date of Birth" value={result.parsed.dob ? result.parsed.dob.toISOString().split('T')[0] : ''} fullWidth disabled />
            </Grid>
          </Grid>
        </Box>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
      <Button onClick={onToggleActive} disabled={loading}>{scanActive ? 'Pause' : 'Resume'}</Button>
      <Button onClick={onApply} disabled={!result || !result.parsed || !result.stable}>Apply</Button>
    </DialogActions>
  </Dialog>
);

export default PatientForm;
